/**
 * ConfluenceLinkPickerModal Component
 *
 * Multi-select picker modal for linking confluences to a playbook.
 * Shows active confluences grouped by market condition category.
 * Each selected confluence has a polarity toggle (positive/negative).
 *
 * Default polarity for newly selected items is 'negative'.
 * Archived linked confluences are preserved on Apply - they are merged back
 * into the selection to prevent silent data loss.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ThumbsUp, ThumbsDown } from 'lucide-react';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  ButtonGroup,
  Link,
  Listbox,
  ListboxItem,
  ListboxSection,
} from '@enfomentis/ui';
import { confluenceService } from '@/services/ConfluenceService';
import { marketConditionCategoryService } from '@/services/MarketConditionCategoryService';
import type { Selection } from '@react-types/shared';
import type { Confluence } from '@/types/schema/Confluence';
import type { MarketConditionCategory } from '@/types/schema/MarketConditionCategory';
import type { PlaybookConfluenceLink } from '@/types/schema/Playbook';

export interface ConfluenceLinkPickerModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Currently selected confluences with polarity (for pre-selection) */
  selectedConfluences: PlaybookConfluenceLink[];
  /** Handler for selection change - receives new array of confluence links */
  onSelectionChange: (confluences: PlaybookConfluenceLink[]) => void;
}

/**
 * Empty state component when no active confluences exist
 */
const EmptyState: React.FC = () => (
  <div className="flex-1 flex items-center justify-center py-12">
    <div className="text-center max-w-md">
      <AlertTriangle className="w-16 h-16 text-text-secondary mx-auto mb-4" />
      <h4 className="text-lg font-medium text-text-primary mb-2">No Confluences Available</h4>
      <p className="text-sm text-text-secondary">
        Create confluences in Reference Data to link them to your playbooks.
      </p>
    </div>
  </div>
);

/**
 * Polarity toggle component for selected confluences
 */
const PolarityToggle: React.FC<{
  polarity: 'positive' | 'negative';
  onToggle: (polarity: 'positive' | 'negative') => void;
}> = ({ polarity, onToggle }) => (
  <ButtonGroup size="sm" className="ml-2">
    <Button
      isIconOnly
      variant={polarity === 'positive' ? 'solid' : 'light'}
      color={polarity === 'positive' ? 'success' : 'default'}
      onPress={() => onToggle('positive')}
      aria-label="Set as positive confluence"
    >
      <ThumbsUp className="w-3 h-3" />
    </Button>
    <Button
      isIconOnly
      variant={polarity === 'negative' ? 'solid' : 'light'}
      color={polarity === 'negative' ? 'warning' : 'default'}
      onPress={() => onToggle('negative')}
      aria-label="Set as negative confluence"
    >
      <ThumbsDown className="w-3 h-3" />
    </Button>
  </ButtonGroup>
);

export const ConfluenceLinkPickerModal: React.FC<ConfluenceLinkPickerModalProps> = ({
  isOpen,
  onClose,
  selectedConfluences,
  onSelectionChange,
}) => {
  const navigate = useNavigate();

  // Local selection state - synced from props when modal opens
  const [localSelectedKeys, setLocalSelectedKeys] = useState<Set<string>>(new Set());

  // Track polarity for each selected confluence
  const [localPolarities, setLocalPolarities] = useState<Map<string, 'positive' | 'negative'>>(
    new Map()
  );

  // Store original selected confluence links to preserve archived on Apply
  const originalSelectedRef = useRef<PlaybookConfluenceLink[]>([]);

  // Data fetching state
  const [activeConfluences, setActiveConfluences] = useState<Confluence[]>([]);
  const [categories, setCategories] = useState<MarketConditionCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Sync selection from props when modal opens
  useEffect(() => {
    if (isOpen) {
      // Store original selected confluences before filtering (to preserve archived on Apply)
      originalSelectedRef.current = [...selectedConfluences];

      // Load data when modal opens
      const loadData = async () => {
        setIsLoading(true);
        try {
          const [confluenceData, categoryData] = await Promise.all([
            confluenceService.getActiveConfluences(),
            marketConditionCategoryService.getAll(),
          ]);
          setActiveConfluences(confluenceData);
          setCategories(categoryData);

          // Pre-select only IDs that exist in the active confluences list
          const activeIds = new Set(confluenceData.map((c) => c.id));
          const validSelection = selectedConfluences.filter((link) => activeIds.has(link.confluenceId));

          setLocalSelectedKeys(new Set(validSelection.map((link) => link.confluenceId)));
          setLocalPolarities(
            new Map(validSelection.map((link) => [link.confluenceId, link.polarity]))
          );
        } catch (error) {
          console.error('Failed to load confluence data:', error);
        } finally {
          setIsLoading(false);
        }
      };

      loadData();
    }
  }, [isOpen, selectedConfluences]);

  // Build category lookup map
  const categoryMap = useMemo(() => {
    const map = new Map<string, MarketConditionCategory>();
    categories.forEach((cat) => map.set(cat.id, cat));
    return map;
  }, [categories]);

  // Group confluences by category, sort categories alphabetically, uncategorized at bottom
  const { categorizedGroups, uncategorized } = useMemo(() => {
    const grouped = new Map<string, Confluence[]>();
    const uncategorizedItems: Confluence[] = [];

    activeConfluences.forEach((item) => {
      if (item.marketConditionCategoryId) {
        const existing = grouped.get(item.marketConditionCategoryId) || [];
        grouped.set(item.marketConditionCategoryId, [...existing, item]);
      } else {
        uncategorizedItems.push(item);
      }
    });

    // Sort each category's items alphabetically by name
    grouped.forEach((items, categoryId) => {
      grouped.set(
        categoryId,
        [...items].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        )
      );
    });

    // Sort uncategorized items alphabetically by name
    const sortedUncategorized = [...uncategorizedItems].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );

    // Sort categories alphabetically by name
    const sortedCategories = Array.from(grouped.entries())
      .map(([categoryId, items]) => {
        const category = categoryMap.get(categoryId);
        return category ? { category, items } : null;
      })
      .filter((g): g is { category: MarketConditionCategory; items: Confluence[] } => g !== null)
      .sort((a, b) =>
        a.category.name.localeCompare(b.category.name, undefined, { sensitivity: 'base' })
      );

    return {
      categorizedGroups: sortedCategories,
      uncategorized: sortedUncategorized,
    };
  }, [activeConfluences, categoryMap]);

  /**
   * Handle selection change from Listbox
   * Guards against HeroUI Listbox emitting "all" for select-all scenarios
   */
  const handleSelectionChange = (keys: Selection): void => {
    // Guard against "all" sentinel before spreading
    if (keys === 'all') return;
    // Filter out any remaining non-string keys for safety
    const newKeys = new Set([...keys].filter((k): k is string => typeof k === 'string'));

    // For newly selected items, default to 'negative' polarity
    const newPolarities = new Map(localPolarities);
    newKeys.forEach((key) => {
      if (!newPolarities.has(key)) {
        newPolarities.set(key, 'negative');
      }
    });

    // Remove polarities for deselected items
    localPolarities.forEach((_, key) => {
      if (!newKeys.has(key)) {
        newPolarities.delete(key);
      }
    });

    setLocalSelectedKeys(newKeys);
    setLocalPolarities(newPolarities);
  };

  /**
   * Handle polarity toggle for a confluence
   */
  const handlePolarityToggle = (confluenceId: string, polarity: 'positive' | 'negative'): void => {
    const newPolarities = new Map(localPolarities);
    newPolarities.set(confluenceId, polarity);
    setLocalPolarities(newPolarities);
  };

  /**
   * Get polarity for a confluence (defaults to 'negative')
   */
  const getPolarity = (confluenceId: string): 'positive' | 'negative' => {
    return localPolarities.get(confluenceId) || 'negative';
  };

  /**
   * Handle apply selection
   * Preserves archived linked confluences by merging them back in
   */
  const handleApply = (): void => {
    // Build links from current selection
    const selectedLinks: PlaybookConfluenceLink[] = Array.from(localSelectedKeys).map(
      (confluenceId) => ({
        confluenceId,
        polarity: getPolarity(confluenceId),
      })
    );

    // Only merge archived links if we successfully loaded confluence data
    // (prevents treating all IDs as archived on fetch failure)
    if (activeConfluences.length > 0) {
      const activeIds = new Set(activeConfluences.map((c) => c.id));
      const archivedLinks = originalSelectedRef.current.filter(
        (link) => !activeIds.has(link.confluenceId)
      );

      // Merge with de-dupe using Map keyed by confluenceId
      const mergedMap = new Map<string, PlaybookConfluenceLink>();
      selectedLinks.forEach((link) => mergedMap.set(link.confluenceId, link));
      archivedLinks.forEach((link) => {
        if (!mergedMap.has(link.confluenceId)) {
          mergedMap.set(link.confluenceId, link);
        }
      });

      onSelectionChange(Array.from(mergedMap.values()));
    } else {
      // On fetch failure, preserve original selection unchanged
      onSelectionChange([...originalSelectedRef.current]);
    }

    onClose();
  };

  /**
   * Handle cancel - just close, selection resets on next open
   */
  const handleCancel = (): void => {
    onClose();
  };

  const hasItems = activeConfluences.length > 0;

  /**
   * Render a confluence item with polarity toggle if selected
   */
  const renderConfluenceItem = (confluence: Confluence, category?: MarketConditionCategory) => {
    const isSelected = localSelectedKeys.has(confluence.id);
    const polarity = getPolarity(confluence.id);

    return (
      <ListboxItem
        key={confluence.id}
        textValue={confluence.name}
        classNames={{
          base: 'py-2 rounded-md my-1 overflow-hidden',
          selectedIcon: 'text-primary scale-150 order-first',
        }}
      >
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 w-full">
          <div className="flex items-center gap-2 min-w-0">
            {category && (
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: category.color }}
              />
            )}
            <span className="font-medium text-text-primary truncate min-w-0">
              {confluence.name}
            </span>
          </div>
          {isSelected && (
            <div onClick={(e) => e.stopPropagation()} className="shrink-0">
              <PolarityToggle
                polarity={polarity}
                onToggle={(p) => handlePolarityToggle(confluence.id, p)}
              />
            </div>
          )}
        </div>
      </ListboxItem>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onClose}
      size="lg"
      scrollBehavior="inside"
      placement="top-center"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <span>Select Confluences</span>
              <span className="text-sm font-normal text-text-secondary">
                Select confluences and set their polarity (positive supports trade, negative argues against)
              </span>
            </ModalHeader>

            <ModalBody className="gap-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-12 text-text-tertiary text-sm">
                  Loading...
                </div>
              ) : !hasItems ? (
                <EmptyState />
              ) : (
                <Listbox
                  aria-label="Confluence items"
                  selectionMode="multiple"
                  selectedKeys={localSelectedKeys}
                  onSelectionChange={handleSelectionChange}
                  variant="light"
                  classNames={{
                    base: 'max-h-[400px] overflow-auto',
                  }}
                >
                  {[
                    // Categorized items (sorted alphabetically by category name)
                    ...categorizedGroups.map(({ category, items }) => (
                      <ListboxSection
                        key={category.id}
                        title={category.name}
                        classNames={{
                          heading:
                            'text-sm font-semibold text-text-secondary flex w-full border-b border-default-200 pb-1',
                          base: 'mb-2',
                        }}
                      >
                        {items.map((item) => renderConfluenceItem(item, category))}
                      </ListboxSection>
                    )),

                    // Uncategorized items at bottom
                    ...(uncategorized.length > 0
                      ? [
                          <ListboxSection
                            key="uncategorized"
                            title="Uncategorized"
                            classNames={{
                              heading:
                                'text-sm font-semibold text-text-tertiary flex w-full border-b border-default-200 pb-1',
                              base: 'mb-2',
                            }}
                          >
                            {uncategorized.map((item) => renderConfluenceItem(item))}
                          </ListboxSection>,
                        ]
                      : []),
                  ]}
                </Listbox>
              )}
            </ModalBody>

            <ModalFooter className="justify-between">
              {/* Manage Confluences shortcut */}
              <Link
                onPress={() => {
                  onClose();
                  navigate('/settings/strategies/reference-data?tab=confluences');
                }}
                showAnchorIcon
                size="sm"
                className="cursor-pointer"
              >
                Manage Confluences
              </Link>

              <div className="flex gap-2">
                <Button variant="light" onPress={handleCancel}>
                  Cancel
                </Button>
                <Button color="primary" onPress={handleApply}>
                  Apply
                </Button>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
